/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {
  Asset
} from '@ion/core/src/types/entities';
import {
  ServiceError
} from '@ion/core/src/types/services';
import {titleCase} from '@ion/core/src/utils/StringUtil';
import {useShowDeletePrompt} from '@ion/dls/src/hooks/useShowDeletePrompt';
import {DeleteResultPrompt} from '@ion/features/src/prompts/DeleteResultPrompt';
import DeletePrompt from './DeletePrompt';

// defines the AssetDeletePrompt Prop type definitions ...
export interface AssetDeletePromptProps {
  show: boolean;
  entity?: string;
  asset: Asset | null;
  result?: Asset | null;
  loading?: boolean;
  error?: ServiceError | null;
  overrideMessage?: Function | null;
  onDeleteConfirm: (asset: Asset) => void;
  onDeleteCancel: () => void;
};

/**
 * The AssetDeletePrompt provides the UI for prompting, confirming, or canceling an Asset
 * Delete action.
 *
 * ```typescript
 *
 *    import {AssetDeletePrompt} from '@ion/dls/src/editors/';
 *
 *    const {
 *      selected,
 *      result,
 *      isEditing,
 *      loading,
 *      error
 *    } = useSelector(({adminAssets: {destroy}}) => destroy);
 *
 *    const onDeleteConfirm = function(asset: Asset) {
 *      dispatch(deleteAsset(selected));
 *    };
 *
 *    function onDeleteCancel() {
 *      dispatch(deleteAdminAsset(selected, false));
 *    }
 *
 *    return (
 *      <AssetDeletePrompt show={isEditing}
 *        asset={selected}
 *        onDeleteConfirm={onDeleteConfirm}
 *        onDeleteCancel={onDeleteCancel}
 *        result={result}
 *        loading={loading}
 *        error={error} />
 *    );
 *
 * ```
 *
 * @param {boolean}       show When true, the asset delete prompt will be displayed.
 * @param {Asset}         asset The selected Asset that is to be deleted.
 * @param {Asset}         result The Asset returned by the service after it has been deleted.
 * @param {boolean}       loading When true, indicates that the asset is currently being
 *                        deleted.
 * @param {ServiceError}  error A reference to the ServiceError If the deletion fails.
 * @param {Function}      onDeleteConfirm callback function which will be invoked with the
 *                        asset when the user confirms the deletion.
 * @param {Function}      onDeleteCancel callback function which will be invoked if the user
 *                        cancels the delete operation.
 */
export const AssetDeletePrompt = ({
  show,
  entity,
  asset,
  result,
  loading,
  error,
  onDeleteConfirm,
  onDeleteCancel,
  overrideMessage = null
}: AssetDeletePromptProps) => {
  const {setShowError, setShowSuccess, showSuccess, showError, closeSnack} = useShowDeletePrompt();

  useEffect(() => {
    setShowError(!!error);
  }, [error]);

  useEffect(() => {
    if (result && !loading) {
      setShowSuccess(true);
    }
  }, [result]);

  return (
    <>
      <DeletePrompt show={show}
        entity={entity}
        items={asset}
        isLoading={loading}
        overrideMessage={overrideMessage}
        onConfirm={function() {
          onDeleteConfirm(asset);
        }}
        onCancel={function() {
          onDeleteCancel();
        }} />
      <DeleteResultPrompt entityTitle={titleCase(entity)}
        error={error}
        isError={showError}
        isSuccess={showSuccess}
        onAction={closeSnack} />
    </>
  );
};

export default AssetDeletePrompt;
